import { gql } from '@apollo/client';
import { Box, Switch, Tooltip, useToast } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { fromError } from '~utils/errors';
import { useToggleProStoreDeveloperAccessMutation } from './__generated__/DeveloperAccessSwitch.graphql';

interface Props {
  organization: {
    id: string;
    name: string;
    proStoreConnected: boolean;
    developerAccess: boolean;
  };
}

/**
 * ProStore developer access for an organization, toggled the same way the admin portal does it.
 */
export function DeveloperAccessSwitch({ organization }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const { verifyUserPermissions } = useAuth();
  const [toggleDeveloperAccess, { loading }] = useToggleProStoreDeveloperAccessMutation();

  const canToggle = verifyUserPermissions([Permission.OrganizationUpdate]);
  const isDisabled = !organization.proStoreConnected || !canToggle || loading;

  const tooltip = !organization.proStoreConnected
    ? t('adminSearchNotConnectedToProStore')
    : !canToggle
    ? t('adminSearchCannotToggleDeveloperAccess')
    : undefined;

  const handleChange = async (enabled: boolean) => {
    try {
      await toggleDeveloperAccess({
        variables: { organizationId: organization.id, enabled },
        update: (cache, { data }) => {
          cache.modify({
            id: cache.identify({
              __typename: 'ServiceAdminOrganizationSearchResult',
              id: organization.id,
            }),
            fields: {
              developerAccess: () => data?.serviceAdminToggleProStoreDeveloperAccess ?? enabled,
            },
          });
        },
      });
    } catch (err) {
      toast({
        status: 'error',
        title: t(
          enabled
            ? 'adminSearchEnableDeveloperAccessError'
            : 'adminSearchDisableDeveloperAccessError',
        ),
        description: fromError(err, 'ToggleProStoreDeveloperAccess', {
          ORGANIZATION_NOT_CONNECTED_TO_PRO_STORE: t('adminSearchNotConnectedToProStore'),
        }),
      });
    }
  };

  return (
    <Tooltip label={tooltip} isDisabled={!tooltip} placement="top">
      <Box display="inline-block">
        <Switch
          isChecked={organization.developerAccess}
          isDisabled={isDisabled}
          onChange={(event) => handleChange(event.target.checked)}
          aria-label={t('prostoreDevAccess')}
        />
      </Box>
    </Tooltip>
  );
}

DeveloperAccessSwitch.graphql = {
  mutations: {
    ToggleProStoreDeveloperAccess: gql`
      mutation ToggleProStoreDeveloperAccess($organizationId: ID!, $enabled: Boolean!) {
        serviceAdminToggleProStoreDeveloperAccess(
          organizationId: $organizationId
          enabled: $enabled
        )
      }
    `,
  },
};
